/* eslint-disable no-use-before-define */
/* eslint-disable @typescript-eslint/no-use-before-define */
import React from 'react';

import { get } from '@lumapps/constants';
import { didRequestFail, didRequestSucceed, didRequestFinish } from '@lumapps/utils/requests';

import { TranslationConfig } from '../types';
import { getAlternativeLanguages, createXMLHttpRequestToRetrieveLanguage, getDefaultLanguage } from '../utils';

declare const window: any;

const config = get();

export interface UseRetrieveTranslationsOptions {
    /**
     * The language code to retrieve translations for (e.g., 'en', 'fr').
     */
    lang?: string;
    /**
     * The default language code to use as a fallback if retrieval fails.
     */
    defaultLanguage?: string;
    /**
     * An array of alternative language codes to try if the main language fails.
     */
    userAlternativeLanguages?: string[];
    /**
     * Callback function invoked when a language retrieval error occurs, receiving the error status code.
     */
    onTranslationsError?: (error: number) => void;
}

export const useRetrieveTranslationsLoader = (
    setTranslationsState: (config: TranslationConfig | undefined) => void,
    options?: UseRetrieveTranslationsOptions,
) => {
    const preferredLanguage = options?.lang || config.userLang;
    const defaultLanguage = options?.defaultLanguage || getDefaultLanguage();
    const userAlternativeLanguages = options?.userAlternativeLanguages || getAlternativeLanguages();

    let retry = 0;

    /**
     * Callback when a language retrieval fails.
     *
     * Alternative languages are based on users' alternative language and sites' languages.
     * Get the next alternative for the user until it works. Fallback to the default language in last resort.
     */
    const onLanguageFail = (failedLanguage: string) => {
        const userAlternativeLang = userAlternativeLanguages[retry] || defaultLanguage;
        retry += 1;

        const setErrorState = () => {
            setTranslationsState({
                error: true,
                loaded: false,
                translations: {},
            });

            if (options?.onTranslationsError) {
                options.onTranslationsError(window.userLangRequest.status || 404);
            }
        };
        // Try user alternative language if not already failed
        if (failedLanguage !== userAlternativeLang) {
            window.userLangRequest = createXMLHttpRequestToRetrieveLanguage(userAlternativeLang, () =>
                onTranslationsLoaded(userAlternativeLang),
            );
            return;
        }

        // Try default language if not already failed
        if (failedLanguage !== defaultLanguage) {
            window.userLangRequest = createXMLHttpRequestToRetrieveLanguage(defaultLanguage, () =>
                onTranslationsLoaded(defaultLanguage, false),
            );
            return;
        }

        // All attempts failed
        setErrorState();
    };

    /**
     * Callback to be executed once the language file is retrieved. Since we are parsing a JSON, we add a try/catch in case
     * something goes wrong.
     */
    const onTranslationsLoaded = (lang: string, retry = true) => {
        if (didRequestSucceed(window.userLangRequest)) {
            const { responseText: responseTextLang } = window.userLangRequest;

            try {
                setTranslationsState({
                    translations: {
                        [`${preferredLanguage}`]: { ...JSON.parse(responseTextLang) },
                    },
                    preferredLanguage,
                });
            } catch (exception) {
                onLanguageFail(lang);
            }
        } else if (didRequestFail(window.userLangRequest) && retry) {
            onLanguageFail(lang);
        }
    };

    React.useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        /**
         * If the debugLang query param is present, we just load nothing as translations, so that
         * the text printed will be the translation key.
         */
        if (queryParams.has('debugLang')) {
            setTranslationsState({
                translations: {
                    [`${preferredLanguage}`]: {},
                },
                preferredLanguage,
                loaded: true,
            });
        } else if (window.userLangRequest) {
            if (didRequestFinish(window.userLangRequest)) {
                onTranslationsLoaded(preferredLanguage);
            } else {
                window.userLangRequest.addEventListener('load', () => onTranslationsLoaded(preferredLanguage));
            }
        } else {
            window.userLangRequest = createXMLHttpRequestToRetrieveLanguage(preferredLanguage, () =>
                onTranslationsLoaded(preferredLanguage),
            );
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    return {
        onTranslationsLoaded,
        onRetryTranslations: () => {
            window.userLangRequest = createXMLHttpRequestToRetrieveLanguage(preferredLanguage, () =>
                onTranslationsLoaded(preferredLanguage),
            );
        },
    };
};

/**
 * This hook adds the different listeners in order to retrieve the information from the
 * translations request. If the request has finished, we trigger the initial state loaded, and if it has not finished,
 * we add a listener to the request and trigger the onLoad when it is the right time.
 *
 * In case there is something wrong with the language bundle, we still need to render something on the page. So if something
 * fails, we will retrieve an alternative language that comes from the `window.USER_ALTERNATIVE_LANGS` variable and retrieve that language instead.
 */
export const useRetrieveTranslations = (options?: UseRetrieveTranslationsOptions) => {
    const [translationState, setTranslationsState] = React.useState<TranslationConfig | undefined>();

    useRetrieveTranslationsLoader(setTranslationsState, options);

    return translationState || null;
};

/** Similar to useRetrieveTranslations but with retry functionality */
export const useRetrieveTranslationsWithRetry = (options?: UseRetrieveTranslationsOptions) => {
    const [translationState, setTranslationsState] = React.useState<TranslationConfig | undefined>();

    const { onRetryTranslations } = useRetrieveTranslationsLoader(setTranslationsState, options);

    return {
        translationsState: translationState || null,
        onRetryTranslations,
    };
};
